import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, Sparkles } from 'lucide-react';
import { AGENTS_FRONT, AGENTS_REVENUE } from '../data/site';
import { AgentAvatar } from './V2Shared';

/* lookup so scenes can reference agents by name */
const BY_NAME = {};
[...AGENTS_FRONT, ...AGENTS_REVENUE].forEach((a) => {
  BY_NAME[a.name] = a;
});

/* each scene: a command someone types, and the agents it spins up */
const SCENES = [
  { prompt: 'Answer every call and fill tomorrow’s schedule.', agents: ['Amy', 'Leena', 'Stephanie', 'Morgan'] },
  { prompt: 'Verify insurance, submit claims, work every denial.', agents: ['Amanda', 'Ariel', 'Sarah', 'Alisha'] },
  { prompt: 'Run the whole practice while we sleep.', agents: ['Amy', 'Amanda', 'Leena', 'Sarah', 'Kianna'] },
];

const TYPE_MS = 48;     // per-character typing speed
const SETTLE_MS = 420;  // pause after typing finishes, before spawning
const HOLD_MS = 4200;   // how long the spawned team stays before the next scene

const V2HeroPrompt = () => {
  const [scene, setScene] = useState(0);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState('typing'); // 'typing' | 'spawning'
  const current = SCENES[scene];

  useEffect(() => {
    const timers = [];
    setTyped('');
    setPhase('typing');

    const full = current.prompt;
    let i = 0;
    const typeInt = setInterval(() => {
      i += 1;
      setTyped(full.slice(0, i));
      if (i >= full.length) {
        clearInterval(typeInt);
        timers.push(setTimeout(() => setPhase('spawning'), SETTLE_MS));
        timers.push(
          setTimeout(() => setScene((s) => (s + 1) % SCENES.length), SETTLE_MS + HOLD_MS)
        );
      }
    }, TYPE_MS);

    return () => {
      clearInterval(typeInt);
      timers.forEach(clearTimeout);
    };
  }, [scene, current.prompt]);

  const spawning = phase === 'spawning';
  const agents = current.agents.map((n) => BY_NAME[n]).filter(Boolean);

  return (
    <div className="relative mx-auto w-full max-w-3xl">
      {/* ambient glow */}
      <div
        aria-hidden
        className="absolute left-1/2 top-0 -z-10 h-[420px] w-[680px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(26,70,229,0.18),transparent_70%)] blur-2xl"
      />

      {/* prompt box */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.21, 0.6, 0.35, 1], delay: 0.35 }}
        className="relative"
      >
        {/* animated gradient ring */}
        <div
          aria-hidden
          className="v2-ring absolute -inset-px rounded-[1.4rem] bg-[linear-gradient(120deg,#1a46e5,#5b7cfa,#1a46e5)] opacity-70 blur-[1px]"
          style={{ backgroundSize: '200% 200%' }}
        />
        <div className="relative rounded-[1.35rem] bg-white p-2 shadow-[0_30px_80px_-30px_rgba(13,35,145,0.45)]">
          <div className="flex items-center gap-3 rounded-[1rem] border border-[#e8edf9] bg-[#fbfcff] px-3.5 py-3.5 md:px-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#1a46e5] shadow-[0_6px_16px_-6px_rgba(26,70,229,0.7)]">
              <Sparkles className="h-[18px] w-[18px] text-white" />
            </span>

            <div className="min-w-0 flex-1 text-left">
              <div className="v2-mono text-[10px] uppercase tracking-[0.16em] text-[#a8bcf0]">
                Ask Asha
              </div>
              <div className="mt-0.5 flex items-center text-[15px] font-medium text-[#0a1633] md:text-[17px]">
                <span className="truncate">{typed}</span>
                <span
                  className={`ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[2px] bg-[#1a46e5] ${
                    spawning ? 'opacity-0' : 'v2-blink'
                  }`}
                />
              </div>
            </div>

            <motion.span
              animate={spawning ? { scale: [1, 0.86, 1] } : {}}
              transition={{ duration: 0.4 }}
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                spawning ? 'bg-[#0f9d76]' : 'bg-[#1a46e5]'
              } shadow-[0_8px_20px_-8px_rgba(26,70,229,0.7)]`}
            >
              <ArrowUp className="h-[18px] w-[18px] text-white" />
            </motion.span>
          </div>
        </div>
      </motion.div>

      {/* status line */}
      <div className="mt-5 flex h-5 items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${scene}-${phase}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.25 }}
            className="v2-mono flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#7c89a8]"
          >
            {spawning ? (
              <>
                <span className="v2-pulse h-1.5 w-1.5 rounded-full bg-[#0f9d76]" />
                Spinning up {agents.length} agents
              </>
            ) : (
              <>
                <span className="h-1.5 w-1.5 rounded-full bg-[#a8bcf0]" />
                Listening…
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* spawned agents */}
      <div className="mt-4 flex min-h-[92px] flex-wrap items-center justify-center gap-3">
        <AnimatePresence mode="popLayout">
          {spawning &&
            agents.map((a, i) => (
              <motion.div
                key={`${scene}-${a.name}`}
                layout
                initial={{ opacity: 0, y: -18, scale: 0.85 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                transition={{ duration: 0.42, delay: i * 0.12, ease: [0.21, 0.6, 0.35, 1] }}
                className="flex items-center gap-3 rounded-2xl border border-[#dde5f3] bg-white px-4 py-2.5 shadow-[0_16px_40px_-20px_rgba(10,22,51,0.3)]"
              >
                <AgentAvatar name={a.name} img={a.img} size="h-9 w-9" ring={false} />
                <div className="text-left">
                  <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#0a1633]">
                    {a.name}
                    <span className="v2-pulse h-1.5 w-1.5 rounded-full bg-[#0f9d76]" />
                  </div>
                  <div className="text-[11.5px] leading-tight text-[#7c89a8]">{a.role}</div>
                </div>
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default V2HeroPrompt;
